/**
 * The API client — every network call in the app goes through here.
 *
 * Temporary shape (AGENTS.md §7): identity travels as the DevAuthGuard's two
 * headers rather than a session. That is exactly why it is centralised in one
 * module: when Better Auth lands, this file changes and nothing else does.
 */
import type {
  Analytics,
  AnalyticsQuery,
  BudgetProfileInput,
  Category,
  CreateCategory,
  CreateEntry,
  Entry,
  EntryList,
  Health,
  Household,
  UpdateHousehold,
  ListEntriesQuery,
  ListProductsQuery,
  CreateMerchant,
  CreatePaymentMeans,
  MerchantList,
  PaymentMeans,
  PeriodSummary,
  ProductHistory,
  ProductList,
  UpdateCategory,
  UpdateEntry,
  AddShoppingListItem,
  CreateProduct,
  CreateShoppingList,
  ShoppingList,
  UpdateMerchant,
  UpdatePaymentMeans,
  UpdateProduct,
  UpdateShoppingList,
  UpdateShoppingListItem,
} from '@et/contracts';

const BASE = '/api';

export interface Identity {
  householdId: string;
  memberId: string;
}

export interface DevIdentities {
  hint: string;
  households: Array<{
    householdId: string;
    name: string;
    baseCurrency: string;
    members: Array<{ memberId: string; name: string; role: string }>;
  }>;
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * `base` exists for exactly one caller: auth. Every ordinary route is proxied
 * at `/api` with the prefix stripped, but Better Auth's routes are proxied
 * untouched at `/v1/auth`, so they must NOT have `/api` glued on the front.
 * A parameter rather than a special case inside the function, because "paths
 * starting with /v1/auth are different" is the kind of rule that gets broken
 * by the next route that happens to start that way.
 */
async function request<T>(
  path: string,
  identity: Identity | null,
  init?: RequestInit,
  base: string = BASE,
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (identity) {
    // With a real session these are no longer the identity — the cookie is.
    // `x-household-id` survives as the HINT the guard already understood: which
    // of your households you would like to act in, checked against your own
    // seats and ignored if it is not one of them. `x-member-id` is derived
    // server-side now and is sent only because AUTH_MODE=dev-headers still
    // wants it; in session mode nothing reads it.
    headers['x-household-id'] = identity.householdId;
    headers['x-member-id'] = identity.memberId;
  }

  let response: Response;
  try {
    // `credentials: 'include'` is what carries the session cookie. Same origin
    // in development (Vite proxies /api), so this is belt and braces there —
    // and load-bearing the moment the API is served from anywhere else.
    response = await fetch(`${base}${path}`, { ...init, headers, credentials: 'include' });
  } catch {
    throw new ApiError('NETWORK', 'Could not reach the API. Is the stack running?');
  }

  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      throw new ApiError('BAD_RESPONSE', 'The API returned something that was not JSON.');
    }
  }

  if (!response.ok) {
    const err = body as { code?: string; message?: string } | null;
    throw new ApiError(err?.code ?? 'HTTP_ERROR', err?.message ?? `Request failed (${response.status})`);
  }

  return body as T;
}

function queryString(query: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const rendered = params.toString();
  return rendered ? `?${rendered}` : '';
}


/**
 * Sign-in, sign-up, sign-out.
 *
 * These are Better Auth's own routes, not this app's controllers, which is why
 * they take no `Identity` — establishing one is the whole point. They live in
 * the same module because this file is the one place that knows how to reach
 * the API (AGENTS.md §7), and an auth call that invented its own fetch would
 * be the one that forgets `credentials`.
 */
export interface SessionUser {
  id: string;
  name: string;
  email: string;
}

/**
 * Auth is NOT under `${BASE}`. Everything else is proxied at /api with the
 * prefix stripped; auth is proxied at /v1/auth untouched, because Better Auth
 * insists the public path and the path it receives are the same string.
 */
const AUTH_BASE = '';

export const authApi = {
  /** The signed-in account, or null. Cheap, and the app's first call. */
  session: () =>
    request<{ user: SessionUser } | null>('/v1/auth/get-session', null, undefined, AUTH_BASE).catch(
      () => null,
    ),

  signUp: (body: { name: string; email: string; password: string }) =>
    request<{ user: SessionUser }>(
      '/v1/auth/sign-up/email',
      null,
      { method: 'POST', body: JSON.stringify(body) },
      AUTH_BASE,
    ),

  signIn: (body: { email: string; password: string }) =>
    request<{ user: SessionUser }>(
      '/v1/auth/sign-in/email',
      null,
      { method: 'POST', body: JSON.stringify(body) },
      AUTH_BASE,
    ),

  signOut: () => request<unknown>('/v1/auth/sign-out', null, { method: 'POST' }, AUTH_BASE),

  /** What this account can do about having no household. */
  onboarding: () =>
    request<{ unclaimed: { id: string; name: string } | null; suggestedName: string }>(
      '/v1/onboarding',
      null,
    ),

  acceptInvite: (token: string) =>
    request<{ ok: boolean; message?: string }>('/v1/invites/accept', null, {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  /** Everywhere this account can act — the switcher's list. */
  myHouseholds: () =>
    request<{
      households: Array<{
        householdId: string;
        name: string;
        currency: string;
        memberId: string;
        role: string;
      }>;
    }>('/v1/households', null),

  /** Start another one. Same call the first-run path uses. */
  newHousehold: (body: { name: string; currency: string }) =>
    request<{ ok: boolean; householdId: string | null; message?: string }>('/v1/households', null, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  profile: () =>
    request<{ id: string; name: string; email: string; image: string | null; avatar: string | null }>(
      '/v1/profile',
      null,
    ),

  updateProfile: (body: { name?: string; avatar?: string | null }) =>
    request<{ ok: boolean; message?: string }>('/v1/profile', null, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  claimHousehold: () =>
    request<{ ok: true }>('/v1/onboarding/claim', null, { method: 'POST' }),

  createHousehold: (body: { name: string; currency: string }) =>
    request<{ ok: true; householdId: string | null }>('/v1/onboarding/household', null, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * What the sign-in screen needs before it can draw itself: whether the
   * server is taking real sessions, and whether Google was configured.
   */
  options: () =>
    request<{ mode: 'session' | 'dev-headers'; google: boolean }>('/v1/sign-in-options', null),

  /**
   * Start the Google round trip.
   *
   * TWO STEPS, NOT A LINK. `sign-in/social` is POST-only: it does not redirect,
   * it RETURNS the Google URL for the browser to go to. An earlier version of
   * this was an `<a href>` pointing straight at the endpoint, which reads
   * nicely — middle-click and long-press work for free on a real link — and
   * 404s, because a GET matches no route. The empty-bodied 404 is the tell that
   * the request reached Better Auth rather than missing the mount.
   *
   * `callbackURL` is where Google sends the person back to once they are done.
   */
  startGoogle: (callbackURL: string) =>
    request<{ url: string; redirect?: boolean }>(
      '/v1/auth/sign-in/social',
      null,
      { method: 'POST', body: JSON.stringify({ provider: 'google', callbackURL }) },
      AUTH_BASE,
    ),
};

export const api = {
  // --- the household as a group of people ---------------------------------
  renameHousehold: (identity: Identity, name: string) =>
    request<{ ok: true }>('/v1/household/name', identity, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),
  invites: (identity: Identity) =>
    request<{ invites: Array<{ id: string; role: string; expiresAt: string; createdAt: string }> }>(
      '/v1/household/invites',
      identity,
    ),
  createInvite: (identity: Identity, body: { role: string; expiresInDays?: number }) =>
    request<{ id: string; role: string; expiresAt: string; token: string }>(
      '/v1/household/invites',
      identity,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  revokeInvite: (identity: Identity, id: string) =>
    request<{ ok: true }>(`/v1/household/invites/${id}`, identity, { method: 'DELETE' }),
  updateMember: (identity: Identity, id: string, body: { role?: string; name?: string }) =>
    request<{ ok: true }>(`/v1/household/members/${id}`, identity, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  removeMember: (identity: Identity, id: string) =>
    request<{ ok: true }>(`/v1/household/members/${id}`, identity, { method: 'DELETE' }),

  health: () => request<Health>('/health', null),
  identities: () => request<DevIdentities>('/v1/dev/identities', null),
  household: (identity: Identity) => request<Household>('/v1/household', identity),
  updateHousehold: (identity: Identity, body: UpdateHousehold) =>
    request<Household>('/v1/household', identity, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  /** Archived included: the Categories screen shows them, every picker filters them. */
  categories: (identity: Identity) =>
    request<Category[]>('/v1/categories?includeArchived=true', identity),
  createCategory: (identity: Identity, body: CreateCategory) =>
    request<Category>('/v1/categories', identity, { method: 'POST', body: JSON.stringify(body) }),
  /** Returns the whole tree, so the caller never merges a partial update. */
  updateCategory: (identity: Identity, id: string, body: UpdateCategory) =>
    request<Category[]>(`/v1/categories/${id}`, identity, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  entries: (identity: Identity, query: Partial<ListEntriesQuery> = {}) =>
    request<EntryList>(`/v1/entries${queryString(query)}`, identity),
  createEntry: (identity: Identity, body: CreateEntry) =>
    request<Entry>('/v1/entries', identity, { method: 'POST', body: JSON.stringify(body) }),
  updateEntry: (identity: Identity, id: string, body: UpdateEntry) =>
    request<Entry>(`/v1/entries/${id}`, identity, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteEntry: (identity: Identity, id: string) =>
    request<null>(`/v1/entries/${id}`, identity, { method: 'DELETE' }),
  budget: (identity: Identity, month: string) =>
    request<PeriodSummary>(`/v1/budget?month=${month}`, identity),
  setBudgetPeriod: (identity: Identity, month: string, body: BudgetProfileInput) =>
    request<BudgetProfileInput>(`/v1/budget/period?month=${month}`, identity, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  paymentMeans: (identity: Identity) => request<PaymentMeans[]>('/v1/payment-means', identity),
  createPaymentMeans: (identity: Identity, body: CreatePaymentMeans) =>
    request<PaymentMeans[]>('/v1/payment-means', identity, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Every write returns the whole list, so nothing merges a partial update. */
  updatePaymentMeans: (identity: Identity, id: string, body: UpdatePaymentMeans) =>
    request<PaymentMeans[]>(`/v1/payment-means/${id}`, identity, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deletePaymentMeans: (identity: Identity, id: string) =>
    request<PaymentMeans[]>(`/v1/payment-means/${id}`, identity, { method: 'DELETE' }),
  /* Shopping lists. Like payment methods, every write answers with every list,
     so two people editing one in a shop converge on the next write. */
  shoppingLists: (identity: Identity) => request<ShoppingList[]>('/v1/shopping-lists', identity),
  createShoppingList: (identity: Identity, body: CreateShoppingList) =>
    request<ShoppingList[]>('/v1/shopping-lists', identity, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateShoppingList: (identity: Identity, id: string, body: UpdateShoppingList) =>
    request<ShoppingList[]>(`/v1/shopping-lists/${id}`, identity, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteShoppingList: (identity: Identity, id: string) =>
    request<ShoppingList[]>(`/v1/shopping-lists/${id}`, identity, { method: 'DELETE' }),
  addShoppingListItem: (identity: Identity, listId: string, body: AddShoppingListItem) =>
    request<ShoppingList[]>(`/v1/shopping-lists/${listId}/items`, identity, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateShoppingListItem: (identity: Identity, itemId: string, body: UpdateShoppingListItem) =>
    request<ShoppingList[]>(`/v1/shopping-lists/items/${itemId}`, identity, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  removeShoppingListItem: (identity: Identity, itemId: string) =>
    request<ShoppingList[]>(`/v1/shopping-lists/items/${itemId}`, identity, { method: 'DELETE' }),
  clearShoppingListChecked: (identity: Identity, listId: string) =>
    request<ShoppingList[]>(`/v1/shopping-lists/${listId}/clear-checked`, identity, {
      method: 'POST',
    }),
  /** Only succeeds for a category nothing points at — see the service. */
  deleteCategory: (identity: Identity, id: string) =>
    request<Category[]>(`/v1/categories/${id}`, identity, { method: 'DELETE' }),
  analytics: (identity: Identity, query: AnalyticsQuery) =>
    request<Analytics>(`/v1/analytics${queryString(query)}`, identity),
  merchants: (identity: Identity) => request<MerchantList>('/v1/merchants', identity),
  /* Every write answers with the whole list, so nothing merges a partial update. */
  createMerchant: (identity: Identity, body: CreateMerchant) =>
    request<MerchantList>('/v1/merchants', identity, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateMerchant: (identity: Identity, id: string, body: UpdateMerchant) =>
    request<MerchantList>(`/v1/merchants/${id}`, identity, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  /** Only succeeds for a merchant nothing points at — see the service. */
  deleteMerchant: (identity: Identity, id: string) =>
    request<MerchantList>(`/v1/merchants/${id}`, identity, { method: 'DELETE' }),
  products: (identity: Identity, query: Partial<ListProductsQuery> = {}) =>
    request<ProductList>(`/v1/products${queryString(query)}`, identity),
  product: (identity: Identity, id: string) => request<ProductHistory>(`/v1/products/${id}`, identity),
  createProduct: (identity: Identity, body: CreateProduct) =>
    request<ProductHistory>('/v1/products', identity, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateProduct: (identity: Identity, id: string, body: UpdateProduct) =>
    request<ProductHistory>(`/v1/products/${id}`, identity, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  /** Only succeeds for an item nothing points at — see the service. */
  deleteProduct: (identity: Identity, id: string) =>
    request<void>(`/v1/products/${id}`, identity, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// Remembering the chosen identity across reloads.
//
// Browser storage can throw outright (private windows, blocked site data), so
// every access is guarded. Losing this is a minor inconvenience, never an error
// worth showing anyone.
// ---------------------------------------------------------------------------
const IDENTITY_KEY = 'et.identity';

export function loadIdentity(): Identity | null {
  try {
    const raw = window.localStorage.getItem(IDENTITY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Identity>;
    if (typeof parsed.householdId === 'string' && typeof parsed.memberId === 'string') {
      return { householdId: parsed.householdId, memberId: parsed.memberId };
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function saveIdentity(identity: Identity): void {
  try {
    window.localStorage.setItem(IDENTITY_KEY, JSON.stringify(identity));
  } catch {
    /* ignore */
  }
}

export function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : String(error);
}
